import type React from 'react';

import { Controller, useFormContext } from 'react-hook-form';
import { AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { HTMLInputTypeAttribute } from 'react';

interface FormInputProps {
  name: string;
  label?: string;
  placeholder?: string;
  type?: HTMLInputTypeAttribute;
  rules?: any;
  className?: string;
  disabled?: boolean;
  defaultValue?: any;
  onValueChange?: (value: any) => void;
  // Additional input props for flexibility
  min?: string | number;
  max?: string | number;
  step?: string | number;
  accept?: string;
  multiple?: boolean;
  autoComplete?: string;
}

export const FormInput = ({
  name,
  label,
  placeholder,
  type = 'text', // Default to text instead of hardcoded date
  rules,
  className,
  disabled,
  defaultValue,
  onValueChange,
  min,
  max,
  step,
  accept,
  multiple,
  autoComplete,
}: FormInputProps) => {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      defaultValue={defaultValue || ''}
      render={({
        field: { onChange, value, ...field },
        fieldState: { error },
      }) => {
        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          let newValue: any = e.target.value;

          // Handle different input types appropriately
          if (type === 'number') {
            newValue = e.target.value === '' ? '' : Number(e.target.value);
          } else if (type === 'checkbox') {
            newValue = e.target.checked;
          } else if (type === 'file') {
            newValue = multiple ? e.target.files : e.target.files?.[0] || null;
          }

          onChange(newValue);
          if (onValueChange) onValueChange(newValue);
        };

        return (
          <div className="space-y-2">
            {label && (
              <label
                htmlFor={name}
                className="text-sm font-medium text-foreground"
              >
                {label}
              </label>
            )}

            <div
              className={cn(
                'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 text-sm ring-offset-background placeholder:text-muted-foreground focus-within:ring-2 focus-within:ring-secondary focus-within:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
                error ? 'border-red-500 ring-1 ring-red-200' : '',
                type === 'file' && 'h-auto py-2',
                className,
              )}
            >
              <input
                type={type}
                id={name}
                {...field}
                value={type === 'file' ? undefined : value || ''}
                onChange={handleChange}
                disabled={disabled}
                placeholder={placeholder}
                min={min}
                max={max}
                step={step}
                accept={accept}
                multiple={multiple}
                autoComplete={autoComplete}
                className={cn(
                  'flex-1 bg-transparent outline-none',
                  type === 'file' ? 'cursor-pointer' : '',
                )}
              />
            </div>

            {error && (
              <p className="text-xs font-medium text-red-500 flex items-center gap-1.5 px-1">
                <AlertCircle className="w-3.5 h-3.5" />
                {error.message || 'This field is required'}
              </p>
            )}
          </div>
        );
      }}
    />
  );
};
